import { useEffect, useRef, useState } from 'react';

/**
 * A blob: address for each photo, so it can be SHOWN — deep-dive F3(h): the step listed
 * IMG_4821.jpg and IMG_4822.jpg by name, and nobody can tell which of those is the storefront.
 *
 * Made once per File and revoked when the photo goes or the screen does: a dozen addresses left
 * behind are a dozen decoded photos the tab keeps holding. A browser (or test DOM) without
 * `URL.createObjectURL` gets no thumbnails, and the names still show.
 */
export function useThumbnails(photos: { name: string; file: File }[]): Record<string, string> {
  const made = useRef(new Map<File, string>());
  const [urls, setUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return;
    const live = new Set(photos.map((p) => p.file));
    for (const [file, url] of made.current) {
      if (!live.has(file)) {
        URL.revokeObjectURL(url);
        made.current.delete(file);
      }
    }
    const next: Record<string, string> = {};
    for (const p of photos) {
      let url = made.current.get(p.file);
      if (!url) {
        url = URL.createObjectURL(p.file);
        made.current.set(p.file, url);
      }
      next[p.name] = url;
    }
    setUrls(next);
  }, [photos]);

  useEffect(() => {
    const all = made.current;
    return () => {
      for (const url of all.values()) URL.revokeObjectURL(url);
      all.clear();
    };
  }, []);

  return urls;
}
